
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Get user's device and browser information
const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;
  
  return {
    userAgent,
    platform,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    language: navigator.language,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };
};

// Attempt to get user's IP address and location (approximation)
const getLocationInfo = async () => {
  try {
    const response = await fetch('https://ipapi.co/json/');
    return await response.json();
  } catch (error) {
    console.error('Error fetching location info:', error);
    return null;
  }
};

// Record user session on login
export const recordUserSession = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) return;
    
    const deviceInfo = getDeviceInfo();
    const locationInfo = await getLocationInfo();
    
    const { error } = await supabase.from('user_sessions').insert({
      user_id: session.user.id,
      ip_address: locationInfo?.ip || null,
      user_agent: deviceInfo.userAgent,
      location: locationInfo ? 
        `${locationInfo.city}, ${locationInfo.region}, ${locationInfo.country_name}` : null,
      device_info: deviceInfo
    });
    
    if (error) throw error;
    
  } catch (error) {
    console.error('Failed to record user session:', error);
  }
};

// Update session on logout
export const updateSessionOnLogout = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) return;
    
    // Get the most recent session for this user
    const { data: userSessions, error: fetchError } = await supabase
      .from('user_sessions')
      .select('*')
      .eq('user_id', session.user.id)
      .order('login_time', { ascending: false })
      .limit(1);
    
    if (fetchError) throw fetchError;
    
    if (userSessions && userSessions.length > 0) {
      const { error: updateError } = await supabase
        .from('user_sessions')
        .update({ logout_time: new Date().toISOString() })
        .eq('id', userSessions[0].id);
      
      if (updateError) throw updateError;
    }
    
  } catch (error) {
    console.error('Failed to update session on logout:', error);
  }
};

let startTime: number;
let currentPage: string;

// Track page view and time spent
export const trackPageView = async (pagePath: string) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) return;
    
    // If tracking was already in progress, record the time spent
    if (startTime && currentPage) {
      const timeSpent = Math.floor((Date.now() - startTime) / 1000); // Time in seconds
      
      if (timeSpent > 0) {
        await supabase.from('user_analytics').insert({
          user_id: session.user.id,
          page_path: currentPage,
          time_spent: timeSpent,
          action_type: 'page_view'
        });
      }
    }
    
    // Start tracking new page
    startTime = Date.now();
    currentPage = pagePath;
    
  } catch (error) {
    console.error('Failed to track page view:', error);
  }
};

// Track specific user actions
export const trackUserAction = async (actionType: string, details?: any) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) return;
    
    await supabase.from('user_analytics').insert({
      user_id: session.user.id,
      page_path: window.location.pathname,
      time_spent: 0, // Not tracking time for specific actions
      action_type: actionType,
      action_details: details || {}
    });
    
  } catch (error) {
    console.error(`Failed to track user action (${actionType}):`, error);
  }
};

// Get user role
export const getUserRole = async (): Promise<string | null> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) return null;
    
    const { data, error } = await supabase
      .from('user_roles')
      .select(`
        role_id,
        role:roles!fk_user_roles_role(name)
      `)
      .eq('user_id', session.user.id)
      .single();
    
    if (error) throw error;
    
    return data?.role?.name || null;
  } catch (error) {
    console.error('Failed to get user role:', error);
    return null;
  }
};

// Function to assign admin role to a user
export const assignAdminRole = async (userId: string) => {
  try {
    // First, get the admin role ID
    const { data: adminRole, error: roleError } = await supabase
      .from('roles')
      .select('id')
      .eq('name', 'Administrator')
      .single();
      
    if (roleError || !adminRole) {
      toast.error('Admin role not found');
      return;
    }
    
    const { error } = await supabase
      .from('user_roles')
      .insert({
        user_id: userId,
        role_id: adminRole.id
      });
    
    if (error) {
      if (error.code === '23505') { // Unique violation error code
        toast.info('User already has admin role');
        return;
      }
      throw error;
    }
    
    toast.success('Admin role assigned successfully');
  } catch (error) {
    console.error('Failed to assign admin role:', error);
    toast.error('Failed to assign admin role');
  }
};

// Function to assign tester role to a user
export const assignTesterRole = async (userId: string) => {
  try {
    // First, get the tester role ID
    const { data: testerRole, error: roleError } = await supabase
      .from('roles')
      .select('id')
      .eq('name', 'Security Analyst') // Changed from 'tester' to match our default roles
      .single();
      
    if (roleError || !testerRole) {
      toast.error('Tester role not found');
      return;
    }
    
    const { error } = await supabase
      .from('user_roles')
      .insert({
        user_id: userId,
        role_id: testerRole.id
      });
    
    if (error) {
      if (error.code === '23505') { // Unique violation error code
        toast.info('User already has tester role');
        return;
      }
      throw error;
    }
    
    toast.success('Tester role assigned successfully');
  } catch (error) {
    console.error('Failed to assign tester role:', error);
    toast.error('Failed to assign tester role');
  }
};

// Check if user is admin
export const isUserAdmin = async (): Promise<boolean> => {
  const role = await getUserRole();
  return role === 'Administrator';
};
